import React, { useEffect, useState } from 'react';
import { SortAscending, Funnel } from 'phosphor-react';
import { DillButton, DillModal } from '../../../general/Dill';
import { useAppDispatch } from '../../../../redux/store';
import { regenerateDillRecommendedTasks } from '../../../../redux/services/collectionActivityService';
import { openDillToast } from '../../../../utils/helpers';

interface ReviseAiRecommendationPromptModalProps {
  open: boolean;
  supplierId: string;
  initialFilterPrompt: string;
  initialPrioritizationPrompt: string;
  initialUseCustomPromptOnly: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type PromptTab = 'prioritization' | 'filtering';

const TABS: { id: PromptTab; label: string; icon: React.ReactNode }[] = [
  { id: 'prioritization', label: 'Prioritization', icon: <SortAscending size={14} /> },
  { id: 'filtering', label: 'Filtering', icon: <Funnel size={14} /> },
];

function ReviseAiRecommendationPromptModal({
  open,
  supplierId,
  initialFilterPrompt,
  initialPrioritizationPrompt,
  initialUseCustomPromptOnly,
  onClose,
  onSuccess,
}: ReviseAiRecommendationPromptModalProps) {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<PromptTab>('prioritization');
  const [filterPrompt, setFilterPrompt] = useState(initialFilterPrompt);
  const [prioritizationPrompt, setPrioritizationPrompt] = useState(initialPrioritizationPrompt);
  const [useCustomPromptOnly, setUseCustomPromptOnly] = useState(initialUseCustomPromptOnly);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setActiveTab('prioritization');
      setFilterPrompt(initialFilterPrompt);
      setPrioritizationPrompt(initialPrioritizationPrompt);
      setUseCustomPromptOnly(initialUseCustomPromptOnly);
    }
  }, [open, initialFilterPrompt, initialPrioritizationPrompt, initialUseCustomPromptOnly]);

  const handleSaveAndRegenerate = async () => {
    setIsSaving(true);
    try {
      const result = await dispatch(
        regenerateDillRecommendedTasks({
          supplierId,
          filterPrompt,
          prioritizationPrompt,
          useCustomPromptOnly,
        })
      );

      if (result.type !== 'collectionActivities/regenerateDillRecommendedTasks/fulfilled') {
        openDillToast({ message: 'AI regeneration failed. Please try again.', type: 'ERROR' });
        return;
      }

      openDillToast({ message: 'Recommendations regenerated', type: 'SUCCESS' });
      onSuccess();
    } catch (error) {
      console.error('Failed to regenerate AI recommendations:', error);
      openDillToast({ message: 'AI regeneration failed. Please try again.', type: 'ERROR' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DillModal
      open={open}
      onClose={() => {
        if (!isSaving) onClose();
      }}
      title="Revise AI Prompt"
      subtitle="Update the AI prompt used to filter and prioritize collection task recommendations. This saves your prompt and regenerates the list now — future recommendations will use it too."
      maxWidth="560px"
      footer={
        <>
          <DillButton label="Cancel" variant="secondary" onClick={onClose} isDisabled={isSaving} />
          <DillButton
            label="Save and Regenerate"
            onClick={handleSaveAndRegenerate}
            isLoading={isSaving}
          />
        </>
      }>
      <div className="flex flex-col gap-3">
        <div className="inline-flex gap-0.5 p-1 bg-gray-100 rounded-lg self-start">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer border-none ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'bg-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'prioritization' ? (
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1.5">Prioritization Prompt</p>
            <textarea
              value={prioritizationPrompt}
              onChange={(e) => setPrioritizationPrompt(e.target.value)}
              placeholder='e.g. "Weight % past due more heavily than dollar amount"'
              disabled={isSaving}
              rows={5}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 disabled:bg-gray-50 disabled:text-gray-400 resize-none"
            />
            <p className="mt-1.5 text-[11px] text-gray-400">
              Controls the order recommended tasks are ranked in — who to prioritize or deprioritize
              among the customers being considered.
            </p>
          </div>
        ) : (
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1.5">Filter Prompt</p>
            <textarea
              value={filterPrompt}
              onChange={(e) => setFilterPrompt(e.target.value)}
              placeholder='e.g. "Only include customers in the Chicago branch" or "Exclude customers under $500 past due"'
              disabled={isSaving}
              rows={5}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 disabled:bg-gray-50 disabled:text-gray-400 resize-none"
            />
            <p className="mt-1.5 text-[11px] text-gray-400">
              Controls which customers are considered for recommendation at all, before
              prioritization is applied.
            </p>
          </div>
        )}

        {activeTab === 'prioritization' && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useCustomPromptOnly}
              onChange={(e) => setUseCustomPromptOnly(e.target.checked)}
              disabled={isSaving}
              className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
            />
            <span className="text-xs text-gray-700">
              Use this prompt instead of the default prioritization
            </span>
          </label>
        )}

        <p className="text-[11px] text-gray-400">Review recommended tasks before acting on them.</p>
      </div>
    </DillModal>
  );
}

export default ReviseAiRecommendationPromptModal;
